import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useQuery from '@hooks/useQuery';
import { resetPassword } from '@api/auth';
import { ApiResponse } from '@typings/ApiResponse';
import SuccessNotificationBanner from '@components/SuccessNotificationBanner';
import ResetPasswordImg from '@assets/images/reset-password-img.png';
import useWindowWidth from '@hooks/useWindowWidth';
import { FormError, SubmitStatus, SubmitStatusEnum } from '@typings/common';
import {
  updateErrors,
  getErrorMessage as getErrorMessageFromErrors,
} from '@utils/commons';
import getErrorMessage from '@utils/errorUtils';
import NewErrorSummary from '@components/NewErrorSummary';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [passInfo, setPassInfo] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [formErrorMessage, setFormErrorMessage] = useState<{
    newPassword: string | undefined;
    confirmPassword: string | undefined;
  }>({
    newPassword: '',
    confirmPassword: '',
  });
  const query = useQuery();
  const token = query.get('token') as string;
  const width = useWindowWidth();

  const [errors, setErrors] = useState<FormError[]>([]);
  const [resetStatus, setResetStatus] = useState<SubmitStatus>({
    loading: false,
    submitted: SubmitStatusEnum.IDLE,
    message: '',
  });

  const onFinish = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    let validationErrors: FormError[] = [];

    validationErrors = updateErrors(
      validationErrors,
      'newPassword',
      passInfo.newPassword.trim() ? '' : 'Please enter your password'
    );
    validationErrors = updateErrors(
      validationErrors,
      'confirmPassword',
      passInfo.confirmPassword.trim() ? '' : 'Please confirm your password'
    );

    if (passInfo.newPassword.trim() !== passInfo.confirmPassword.trim()) {
      validationErrors = updateErrors(
        validationErrors,
        'confirm',
        'Please make sure that your new password is exactly the same in both boxes.'
      );
    }
    setErrors(prev => validationErrors);
    if (validationErrors.length !== 0) {
      return;
    }
    setResetStatus(prev => ({ ...prev, loading: true }));

    try {
      const response: ApiResponse<null> = await resetPassword(
        token,
        passInfo.newPassword.trim()
      );
      if (response && response.status === 'success') {
        setResetStatus({
          loading: false,
          submitted: SubmitStatusEnum.SUCCESS,
          message: 'Password reset successful. Redirecting to login',
        });
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);
      setResetStatus({
        loading: false,
        submitted: SubmitStatusEnum.FAILED,
        message: errorMessage,
      });

      validationErrors = updateErrors(
        validationErrors,
        'resetPasswordAPI',
        errorMessage
      );
      setErrors(prev => validationErrors);
    } finally {
      setResetStatus(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    setFormErrorMessage({
      newPassword: getErrorMessageFromErrors(errors, 'newPassword'),
      confirmPassword: getErrorMessageFromErrors(errors, 'confirmPassword'),
    });
  }, [errors]);

  return (
    <div className="govuk-width-container mt-5 mb-5 md:mb-10">
      <h1 className="govuk-heading-l">Reset Password</h1>

      <div className="govuk-grid-row">
        {resetStatus.submitted === SubmitStatusEnum.SUCCESS && (
          <SuccessNotificationBanner
            title={resetStatus.message}
            duration={3000}
          />
        )}
        {width < 641 && (
          <div className="govuk-grid-column-one-half mb-3">
            <img
              src={ResetPasswordImg}
              alt="Reset Password illustration"
              className="govuk-image"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        )}
        <div className="govuk-grid-column-one-half">
          <p className="govuk-body">
            Please enter a new MyWishes Care password in the boxes below. Once
            changed you will be redirected to the login page and able login
            using your new password.
          </p>

          <form onSubmit={onFinish} noValidate>
            {errors.length > 0 && <NewErrorSummary errors={errors} />}
            <div
              className={`govuk-form-group ${formErrorMessage.newPassword ? 'govuk-form-group--error' : ''}`}
            >
              <label className="govuk-label" htmlFor="newPassword">
                Your new password
              </label>
              {formErrorMessage.newPassword && (
                <span id="password-error" className="govuk-error-message">
                  <span className="govuk-visually-hidden">Error:</span>{' '}
                  {formErrorMessage.newPassword}
                </span>
              )}
              <input
                className={`govuk-input ${formErrorMessage.newPassword ? 'govuk-input--error' : ''}`}
                id="newPassword"
                name="newPassword"
                type="password"
                autoComplete="new-password"
                placeholder="Enter your new password"
                aria-describedby={
                  formErrorMessage.newPassword ? 'password-error' : undefined
                }
                value={passInfo.newPassword}
                onChange={e =>
                  setPassInfo(prev => ({
                    ...prev,
                    newPassword: e.target.value,
                  }))
                }
              />
            </div>

            <div
              className={`govuk-form-group ${formErrorMessage.confirmPassword ? 'govuk-form-group--error' : ''}`}
            >
              <label className="govuk-label" htmlFor="confirmPassword">
                Please confirm your new password
              </label>
              {formErrorMessage.confirmPassword && (
                <span id="confirm-error" className="govuk-error-message">
                  <span className="govuk-visually-hidden">Error:</span>{' '}
                  {formErrorMessage.confirmPassword}
                </span>
              )}
              <input
                className={`govuk-input ${formErrorMessage.confirmPassword ? 'govuk-input--error' : ''}`}
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="confirmPassword"
                placeholder="Enter your new password again"
                aria-describedby={
                  formErrorMessage.confirmPassword ? 'confirm-error' : undefined
                }
                value={passInfo.confirmPassword}
                onChange={e =>
                  setPassInfo(prev => ({
                    ...prev,
                    confirmPassword: e.target.value,
                  }))
                }
              />
            </div>

            <button
              type="submit"
              className="govuk-button govuk-button--primary"
              disabled={resetStatus.loading}
            >
              {resetStatus.loading ? 'Processing...' : 'Confirm new password'}
            </button>
          </form>
        </div>
        {width >= 641 && (
          <div className="govuk-grid-column-one-half">
            <img
              src={ResetPasswordImg}
              alt="Reset Password illustration"
              className="govuk-image"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
